import { Pencil } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { UseCaseDetail, Persona } from '../../../types';
import PersonaDisplaySection from '../PersonaDisplaySection';
import ExcalidrawCanvas, { ExcalidrawData } from '../../Common/ExcalidrawCanvas';
import MarkdownEditor from '../../Common/MarkdownEditor';
import MarkdownDisplay from '../../Common/MarkdownDisplay';

import { useTranslation } from 'react-i18next';
import { Button } from '../../Common/button';

const PREDEFINED_TOOLS = ['Teams', 'Slack', 'Gmail', 'Outlook', 'SAP', 'Salesforce', 'Jira', 'Confluence', 'Excel', 'PowerPoint', 'Word'];

interface UseCaseBusinessDescriptionTabProps {
  useCase: UseCaseDetail;
  editedBusinessNeed: string;
  editedInput: string;
  editedOutput: string;
  editedInvolvedTools: string[];
  customToolInput: string;
  editedTechElements: string;
  excalidrawData: ExcalidrawData | null;
  isLoadingFlow: boolean;
  errorFlow: string | null;
  onBusinessNeedChange: (value: string) => void;
  onInputChange: (value: string) => void;
  onOutputChange: (value: string) => void;
  onInvolvedToolsChange: (newTools: string[]) => void;
  onCustomToolInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAddCustomTool: () => void;
  onTechElementsChange: (value: string) => void;
  setExcalidrawData: (data: ExcalidrawData) => void;
  onSaveFlowDiagram?: () => Promise<void>;
  associatedPersonas: Persona[];
  isLoadingPersonas: boolean;
  errorPersonas: string | null;
  updateAssociatedPersonas: (personaIds: string[]) => Promise<boolean>;
  fetchAssociatedPersonas: () => Promise<void>;
  onSave: () => Promise<void>;
}

const UseCaseBusinessDescriptionTab: React.FC<UseCaseBusinessDescriptionTabProps> = ({
  useCase,
  editedBusinessNeed,
  editedInput,
  editedOutput,
  editedInvolvedTools,
  customToolInput,
  editedTechElements,
  excalidrawData,
  isLoadingFlow,
  errorFlow,
  onBusinessNeedChange,
  onInputChange,
  onOutputChange,
  onInvolvedToolsChange,
  onCustomToolInputChange,
  onAddCustomTool,
  onTechElementsChange,
  setExcalidrawData,
  onSaveFlowDiagram,
  associatedPersonas,
  isLoadingPersonas,
  errorPersonas,
  updateAssociatedPersonas,
  fetchAssociatedPersonas,
  onSave,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [canvasKey, setCanvasKey] = useState(Date.now());

  const handleSave = async () => {
    if (onSaveFlowDiagram) await onSaveFlowDiagram();
    await onSave();
    setIsEditing(false);
  };

  useEffect(() => {
    setCanvasKey(Date.now());
  }, [isEditing]);

  // Helper pour gérer les changements de checkbox
  const handleToolCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, tool: string) => {
    const isChecked = e.target.checked;
    const newTools = isChecked
      ? [...editedInvolvedTools, tool]
      : editedInvolvedTools.filter(t => t !== tool);
    onInvolvedToolsChange(newTools);
  };

  // Helper pour retirer un outil
  const handleRemoveTool = (toolToRemove: string) => {
    const newTools = editedInvolvedTools.filter(t => t !== toolToRemove);
    onInvolvedToolsChange(newTools);
  };


  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-6">
      <div className="flex items-center justify-between border-b border-border pb-2">
        <h2 className="text-xl font-semibold text-foreground">
          {t('useCaseBusiness.title')}
        </h2>
        {!isEditing ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(true)}>
            <Pencil className="h-4 w-4 mr-1" />
            {t('global.edit')}
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" onClick={handleSave}>
              {t('global.save')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(false)}>
              {t('global.cancel')}
            </Button>
          </div>
        )}
      </div>

      {/* Section Besoin Métier */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-2">
          {t('useCaseBusiness.businessNeedTitle')}
        </h3>
        {isEditing ? (
          <MarkdownEditor
            value={editedBusinessNeed}
            onChange={onBusinessNeedChange}
            height={160}
            placeholder={t('useCaseBusiness.businessNeedTitle')}
            className="w-full"
          />
        ) : (
          <MarkdownDisplay content={useCase.business_need} emptyMessage={t('global.notDefined')} className="text-muted-foreground" />
        )}
      </div>

      {/* Section Éléments Techniques (Déplacée ici) */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-2">
          {t('useCaseBusiness.techTitle')}
        </h3>
        {isEditing ? (
          <MarkdownEditor
            value={editedTechElements}
            onChange={onTechElementsChange}
            height={160}
            placeholder={t('useCaseBusiness.techPlaceholder')}
            className="w-full"
          />
        ) : (
          <MarkdownDisplay content={useCase.technical_elements} emptyMessage={t('global.notDefined')} className="text-muted-foreground" />
        )}
      </div>

      {/* Section Input */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-2">
          {t('useCaseBusiness.inputTitle')}
        </h3>
        {isEditing ? (
          <MarkdownEditor
            value={editedInput}
            onChange={onInputChange}
            height={120}
            placeholder={t('useCaseBusiness.inputTitle')}
            className="w-full"
          />
        ) : (
          <MarkdownDisplay content={useCase.input} emptyMessage={t('global.notDefined')} className="text-muted-foreground" />
        )}
      </div>

      {/* Section Output */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-2">
          {t('useCaseBusiness.outputTitle')}
        </h3>
        {isEditing ? (
          <MarkdownEditor
            value={editedOutput}
            onChange={onOutputChange}
            height={120}
            placeholder={t('useCaseBusiness.outputTitle')}
            className="w-full"
          />
        ) : (
          <MarkdownDisplay content={useCase.output} emptyMessage={t('global.notDefined')} className="text-muted-foreground" />
        )}
      </div>

       {/* Section Outils Concernés */}
       <div>
         <h3 className="text-lg font-medium text-foreground mb-2">
           {t('useCaseBusiness.toolsTitle')}
         </h3>
         {isEditing ? (
           <div className="space-y-4">
             {/* Cases à cocher pour outils prédéfinis */}
             <div>
               <h4 className="text-sm font-medium text-muted-foreground mb-2">
                 {t('useCaseBusiness.commonToolsTitle')}
               </h4>
               <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                 {PREDEFINED_TOOLS.map(tool => (
                   <label key={tool} className="flex items-center space-x-2 text-sm">
                     <input
                       type="checkbox"
                       checked={editedInvolvedTools.includes(tool)}
                       onChange={(e) => handleToolCheckboxChange(e, tool)}
                       className="rounded border-input text-primary shadow-sm focus:border-ring focus:ring focus:ring-ring focus:ring-opacity-50"
                     />
                     <span>{tool}</span>
                   </label>
                 ))}
               </div>
             </div>

             {/* Ajout d'outil personnalisé */}
             <div>
               <h4 className="text-sm font-medium text-muted-foreground mb-2">
                 {t('useCaseBusiness.addToolTitle')}
               </h4>
               <div className="flex items-center space-x-2">
                 <input
                   type="text"
                   value={customToolInput}
                   onChange={onCustomToolInputChange}
                   placeholder={t('useCaseBusiness.toolPlaceholder')}
                   className="flex-grow p-2 border border-input rounded-md focus:ring-2 focus:ring-ring text-sm bg-background text-foreground"
                 />
                 <button
                   type="button"
                   onClick={onAddCustomTool}
                   className="px-3 py-2 bg-primary/10 text-primary rounded-md hover:bg-primary/20 text-sm font-medium"
                 >
                   {t('useCaseBusiness.addToolButton')}
                 </button>
               </div>
             </div>

             {/* Liste des outils sélectionnés/ajoutés */}
             {editedInvolvedTools.length > 0 && (
               <div>
                 <h4 className="text-sm font-medium text-muted-foreground mb-2">
                   {t('useCaseBusiness.selectedToolsTitle')}
                 </h4>
                 <div className="flex flex-wrap gap-2">
                   {editedInvolvedTools.map((tool, index) => (
                     <span key={index} className="flex items-center px-2 py-1 bg-muted text-foreground rounded-md text-sm">
                       {tool}
                       <button
                         type="button"
                         onClick={() => handleRemoveTool(tool)}
                         className="ml-1.5 text-muted-foreground hover:text-red-500"
                         aria-label={`Retirer ${tool}`}
                       >
                         &times; {/* Symbole croix */}
                       </button>
                     </span>
                   ))}
                 </div>
               </div>
             )}
           </div>
         ) : (
           // Affichage en mode lecture (inchangé)
           <div className="flex flex-wrap gap-2">
             {(useCase.involved_tools && useCase.involved_tools.length > 0) ? ( // Correction: involvedTools -> involved_tools
               useCase.involved_tools.map((tool, index) => (
                 <span key={index} className="px-2 py-1 bg-muted text-foreground rounded-md text-sm">
                   {tool}
                 </span>
               ))
             ) : (
               <p className="text-muted-foreground italic text-sm">
                 {t('useCaseBusiness.noTools')}
               </p>
             )}
           </div>
         )}
       </div>


       {/* Composant Personas (utilise les données du hook) */}
       <div>
        {isLoadingPersonas && (
          <p className="text-muted-foreground italic">
            {t('useCaseBusiness.personasLoading')}
          </p>
        )}
        {errorPersonas && (
          <p className="text-red-500 italic">
            {t('useCaseBusiness.personasErrorPrefix')} {errorPersonas}
          </p>
        )}
         {!isLoadingPersonas && !errorPersonas && (
           <PersonaDisplaySection
             personas={associatedPersonas}
             updateAssociatedPersonas={updateAssociatedPersonas}
             fetchAssociatedPersonas={fetchAssociatedPersonas}
             showTitle={false}
             showButton={false}
             renderAddButton={(onClick, disabled) => (
               <div className="flex justify-between items-center mb-4">
                 <h3 className="text-lg font-medium text-foreground">
                   {t('useCaseBusiness.personasTitle')}
                 </h3>
                 <Button type="button" variant="default" size="sm" onClick={onClick} disabled={disabled} className="bg-primary text-primary-foreground hover:bg-primary/90">
                   {t('useCaseBusiness.addPersona')}
                 </Button>
               </div>
             )}
           />
         )}
       </div>
      <div className="mt-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-medium text-foreground">
            {t('useCaseBusiness.flowTitle')}
          </h3>
          <div className="flex items-center space-x-2">
            {errorFlow && <p className="text-sm text-red-600 italic">{errorFlow}</p>}
            {isLoadingFlow && (
              <p className="text-sm text-muted-foreground italic">
                {t('useCaseBusiness.flowSaving')}
              </p>
            )}
          </div>
        </div>
        <ExcalidrawCanvas
          key={canvasKey}
          initialData={excalidrawData}
          readOnly={!isEditing}
          onChange={setExcalidrawData}
        />
      </div>
    </div>
  );
};

export default UseCaseBusinessDescriptionTab;
